import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ntd, dt } from '@/lib/format';
import {
  Card,
  Empty,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
} from '@/components/ui';
import { useAuth } from '@/store/auth';

interface Sum {
  count: number;
  cents: number;
}
interface DailyClose {
  date: string;
  netCashCents: number;
  byMethod: { method: string; depositCents: number; refundCents: number; netCents: number }[];
  deposits: Sum;
  migrationDeposits: Sum;
  consumes: Sum;
  refunds: Sum;
  tickets: { granted: number; consumed: number; refunded: number };
  invoices: { count: number; amountCents: number; taxCents: number };
  entries: {
    id: string;
    type: 'DEPOSIT' | 'CONSUME' | 'REFUND';
    amountCents: number;
    isMigration: boolean;
    payMethod: string;
    customerName: string;
    at: string;
  }[];
}

const METHOD_LABEL: Record<string, string> = {
  CASH: '現金',
  TRANSFER: '轉帳',
  CARD: '刷卡',
  OTHER: '其他',
};

const TYPE: Record<string, { label: string; tone: Parameters<typeof Tag>[0]['tone']; sign: string }> = {
  DEPOSIT: { label: '儲值', tone: 'wallet', sign: '+' },
  CONSUME: { label: '核銷', tone: 'ticket', sign: '−' },
  REFUND: { label: '退款', tone: 'danger', sign: '−' },
};
const today = () => new Date().toISOString().slice(0, 10);

export function DailyClosePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const [date, setDate] = useState(today());

  const close = useQuery({
    queryKey: ['daily-close', date],
    queryFn: () => api.get<DailyClose>(`/finance/daily-close?date=${date}`),
    enabled: isAdmin && /^\d{4}-\d{2}-\d{2}$/.test(date),
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="日結 / 現金對帳" subtitle="當日進帳 · 核銷 · 退款 · 發票" />
        <Card>
          <Empty>🔒 日結屬財務機密,僅租戶管理者可檢視。</Empty>
        </Card>
      </div>
    );
  }

  const d = close.data;

  return (
    <div>
      <PageHeader
        title="日結 / 現金對帳"
        subtitle="打烊對帳 · 當日新收現金 = 營運儲值 − 退款(排除遷移資料)"
        right={
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ ...inputStyle, width: 160 }}
          />
        }
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="當日新收現金淨額" value={ntd(d?.netCashCents ?? 0)} tone="primary" hint="儲值 − 退款" />
        <Stat label="新收儲值" value={ntd(d?.deposits.cents ?? 0)} tone="wallet" hint={`${d?.deposits.count ?? 0} 筆`} />
        <Stat label="核銷兌現" value={ntd(d?.consumes.cents ?? 0)} tone="ticket" hint={`${d?.consumes.count ?? 0} 筆 · 已收款服務`} />
        <Stat label="退款" value={ntd(d?.refunds.cents ?? 0)} tone={d && d.refunds.cents > 0 ? 'danger' : 'default'} hint={`${d?.refunds.count ?? 0} 筆`} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="開立發票" value={`${d?.invoices.count ?? 0} 張`} hint={ntd(d?.invoices.amountCents ?? 0)} />
        <Stat label="其中營業稅" value={ntd(d?.invoices.taxCents ?? 0)} hint="5%" />
        <Stat label="堂票發放 / 核銷" value={`${d?.tickets.granted ?? 0} / ${d?.tickets.consumed ?? 0}`} hint="非現金異動" />
        <Stat label="遷移儲值(不計新收)" value={ntd(d?.migrationDeposits.cents ?? 0)} hint={`${d?.migrationDeposits.count ?? 0} 筆`} />
      </div>

      <Card title="付款方式分列(打烊對帳:現金抽屜應有金額)" icon="💵">
        {!d?.byMethod.length ? (
          <Empty>當日無收款。</Empty>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {d.byMethod.map((m) => (
              <div key={m.method} className="card" style={{ padding: 14, flex: 1, minWidth: 130 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{METHOD_LABEL[m.method] ?? m.method}</div>
                <div className="num" style={{ fontSize: 20, fontWeight: 700, color: m.method === 'CASH' ? 'var(--primary-deep)' : 'var(--ink)' }}>
                  {ntd(m.netCents)}
                </div>
                {m.refundCents > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--danger)' }}>含退款 −{ntd(m.refundCents)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={`${date} 當日明細`} icon="🧾">
        {!d?.entries.length ? (
          <Empty>當日無金流異動。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>時間</th>
                <th style={thStyle}>類型</th>
                <th style={thStyle}>顧客</th>
                <th style={thStyle}>金額</th>
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e) => {
                const t = TYPE[e.type];
                return (
                  <tr key={e.id}>
                    <td style={cellStyle} className="num">{dt(e.at)}</td>
                    <td style={cellStyle}>
                      <Tag tone={t.tone}>{t.label}</Tag>
                      {e.type !== 'CONSUME' && (
                        <Tag tone="default" style={{ marginLeft: 4 }}>{METHOD_LABEL[e.payMethod] ?? e.payMethod}</Tag>
                      )}
                      {e.isMigration && <Tag tone="default" style={{ marginLeft: 4 }}>遷移</Tag>}
                    </td>
                    <td style={cellStyle}>{e.customerName}</td>
                    <td className="num" style={{ ...cellStyle, fontWeight: 600 }}>
                      {t.sign}{ntd(e.amountCents)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8 }}>
        ※ 系統目前未紀錄付款方式(現金 / 刷卡 / 行動支付),故無法拆分收款管道——屬後續強化項。
      </div>
    </div>
  );
}
